// src/v2/hooks/useBIR0619F.js
import { useState, useCallback } from 'react';
import reportsService from '../services/reportsService';
import { useGeneralMessage } from '../../common/APICommon';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

export const useBIR0619F = () => {
  const [loading, setLoading] = useState(false);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  /**
   * Scenario A: Download the raw template directly
   */
  const downloadBlankTemplate = useCallback((month, year) => {
    const pdfUrl = `${window.location.origin}/docs/BIR0619-F.pdf`;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `BIR0619-F_${month}_${year}_Blank.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showGeneralMessage("Blank template downloaded (No records found).", true);
  }, [showGeneralMessage]);

  /**
   * Scenario B: Fill the template with data
   */
  const fillAndDownloadPDF = useCallback(async (data, month, year, companyInfo) => {
    try {
      const pdfUrl = `${window.location.origin}/docs/BIR0619-F.pdf`;
      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error("Template file not found");

      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const page = pdfDoc.getPages()[0];
      const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // --- COORDINATE MAPPING (Identical layout to E for headers) ---
      const mm = month.padStart(2, '0');
      page.drawText(mm[0], { x: 73, y: 818, size: 10, font });
      page.drawText(mm[1], { x: 88, y: 818, size: 10, font });
      const yyyy = year.toString();
      page.drawText(yyyy[0], { x: 108, y: 818, size: 10, font });
      page.drawText(yyyy[1], { x: 123, y: 818, size: 10, font });
      page.drawText(yyyy[2], { x: 138, y: 818, size: 10, font });
      page.drawText(yyyy[3], { x: 153, y: 818, size: 10, font });

      if (companyInfo?.tin) {
        const tinDigits = companyInfo.tin.replace(/\D/g, '');
        let startX = 238;
        for (let i = 0; i < Math.min(tinDigits.length, 9); i++) {
          page.drawText(tinDigits[i], { x: startX + (i * 18.5), y: 785, size: 10, font });
          if ((i + 1) % 3 === 0) startX += 5;
        }
      }

      page.drawText(companyInfo?.name || "", { x: 25, y: 758, size: 9, font });
      page.drawText(companyInfo?.address || "", { x: 25, y: 732, size: 8, font });

      // Computation (Part II)
      const totalTax = data.reduce((sum, item) => sum + (item.taxWithheld || 0), 0);
      const taxStr = totalTax.toLocaleString('en-US', { minimumFractionDigits: 2 });
      page.drawText(taxStr, { x: 480, y: 633, size: 10, font });
      page.drawText(taxStr, { x: 480, y: 550, size: 10, font });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `BIR0619-F_${month}_${year}.pdf`;
      link.click();

      showGeneralMessage("Report generated and downloaded.", true);
    } catch (error) {
      console.error(error);
      showGeneralMessage("Error filling PDF. Downloading blank instead.", false);
      downloadBlankTemplate(month, year);
    }
  }, [showGeneralMessage, downloadBlankTemplate]);

  const generateAndDownload = useCallback(async (params, month, year, companyInfo) => {
    setLoading(true);
    try {
      let data = [];
      try {
        const res = await reportsService.getReportData('BIR0619F', params);
        data = res || [];
      } catch (apiErr) {
        data = []; // Fallback for demo
      }

      if (data.length === 0) {
        downloadBlankTemplate(month, year);
      } else {
        await fillAndDownloadPDF(data, month, year, companyInfo);
      }
    } catch (err) {
      showGeneralMessage("Failed to process report.", false);
    } finally {
      setLoading(false);
    }
  }, [fillAndDownloadPDF, downloadBlankTemplate, showGeneralMessage]);

  return { loading, generateAndDownload, showMessage, message, isSuccess };
};